import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";

export default function Home() {
  const [passGroup, setPassGroup] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const PASS_WAIVER_FORM_ID = "696e5cf7c2815b2bf6f0b4db";

  const regex = /^[0-9]{6}-[0-9A-NP-Z]{8}$/; // no O allowed

  // Handle typing in the input
  const handleInputChange = (e) => {
    let value = e.target.value.toUpperCase();
    const cursorPosition = e.target.selectionStart;
    const prevValue = passGroup;
    const isDeleting = value.length < prevValue.length;

    // Only allow digits, letters (except O), and hyphen
    value = value.replace(/[^0-9A-NP-Z-]/g, "");

    // Strip hyphen for processing
    let raw = value.replace(/-/g, "");

    let firstPart = raw.slice(0, 6).replace(/[^0-9]/g, "");
    let secondPart = raw.slice(6, 14).replace(/[^0-9A-NP-Z]/g, "");

    let result = firstPart;

    if (firstPart.length === 6) {
      if (isDeleting) {
        if (secondPart.length > 0) result += "-" + secondPart;
      } else {
        result += "-" + secondPart;
      }
    }

    setPassGroup(result);

    // Keep cursor just past the hyphen when auto-inserted
    if (!isDeleting && result.length === 7 && cursorPosition === 6) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(7, 7);
        }
      }, 0);
    }
  };

  // Local format validation
  useEffect(() => {
    setIsValid(regex.test(passGroup));
  }, [passGroup]);

  // Debounced backend existence check
  useEffect(() => {
    if (!isValid) {
      setExists(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const debounceTimer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/waivers/${passGroup}`,
          { signal: controller.signal }
        );
        setExists(res.ok);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error(err);
          setExists(false);
        }
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(debounceTimer);
      controller.abort();
    };
  }, [passGroup, isValid]);

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isValid && exists) {
        navigate(`/forms/${PASS_WAIVER_FORM_ID}?passGroup=${passGroup}`);
      }
    }
  };

  return (
    <div className="h-screen relative isolate overflow-hidden bg-gray-900">
      <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-balance text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Select a form to complete
          </h2>

          {/* Existing form links */}
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Link
              to="/forms/677ff7127403ba992b4cb661"
              className="w-1/3 rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Tubing Waiver
            </Link>
          </div>

          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Link
              to="/forms/67884c0b3a08252b46be5acf"
              className="w-1/3 rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Lessons Waiver
            </Link>
          </div>

          <div className="mt-10 flex items-center justify-center gap-x-6">
            <Link
              to="/forms/67888da03a08252b46be5ae1"
              className="w-1/3 rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Rental Form & Waiver
            </Link>
          </div>

          {/* Pass group input */}
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <div className="relative w-1/3">
              <input
                ref={inputRef}
                type="text"
                value={passGroup}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="111111-A1A1A1A1"
                maxLength={15}
                className="w-full rounded-md px-3.5 py-2.5 pr-16 text-sm font-semibold text-gray-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white uppercase tracking-wider"
              />
              <Link
                to={
                  isValid && exists
                    ? `/forms/${PASS_WAIVER_FORM_ID}?passGroup=${passGroup}`
                    : "#"
                }
                onClick={(e) => {
                  if (!isValid || !exists) e.preventDefault();
                }}
                className={`absolute right-1 top-1/2 transform -translate-y-1/2 rounded-md px-2 py-1 text-xs font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  isValid && exists
                    ? "bg-gray-900 text-white hover:bg-gray-800 focus-visible:outline-gray-900"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {loading ? "Checking..." : "Go"}
              </Link>
              
            </div>
          </div>
          <div>

            {isValid && !loading && !exists && (
                <p className="text-purple-600 text-sm mt-6">
                  Pass group not found.
                </p>
              )}
            </div>
        </div>
      </div>
    </div>
  );
}
